import { setInsightsLogicalId } from '../actions/internalActions';
import getInsightsTraceId from '../selectors/getInsightsTraceId';
import getScenarioEndpoint from '../selectors/getScenarioEndpoint';
import { getMeetingInsights } from '../selectors/meetingInsightsSelectors';
import { getMaxInsightsToShowOnInitialRender } from '../selectors/meetingInsightsConsts';
import EmailInsightType from '../store/schema/EmailInsightType';
import FileInsightType from '../store/schema/FileInsightType';
import {
    default as InsightsDataFetchSource,
    convertInsightsEntrySourceEnumToName,
} from '../store/schema/InsightsDataFetchSource';
import { getGuid } from 'owa-guid';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import {
    lazyLogCachedContentRendered,
    lazyLogClientLayout,
    lazyLogResponseReceivedV2,
    lazyLogResultsRendered,
    lazyLogSearchActions,
    lazyLogSearchEntityActions,
    lazyLogSearchFeedbackActions,
    ResultsView,
} from 'owa-search-instrumentation';
import { SubstrateSearchScenario } from 'owa-search-service';
import {
    getInitialRequestTime,
    getLogicalId,
} from '../selectors/meetingInsightsInstrumentationSelectors';

/**
 * Logged whenever you receive response from recommendations
 * @param traceId from recommendation service response
 * @param latency e2etime that's taken in between client sending out request til response received
 * @param status the response status code
 * @param insightsEntrySource where the insigths recommendation service call is triggered from
 */
export function logInsightsResponseReceived(
    meetingId: string,
    traceId: string,
    latency: number,
    status: string,
    insightsEntrySource: InsightsDataFetchSource
) {
    const scenario: SubstrateSearchScenario = getScenarioEndpoint(insightsEntrySource);
    lazyLogResponseReceivedV2.importAndExecute(scenario, traceId, latency, status);
}

/**
 * Logged whenever client tries to render recommendations results either from the response or cache
 * @param meetingId that insights are shown for
 * @param insightsEntrySource where the insigths recommendation service call is triggered from
 */
export function logInsightsResponseRendered(
    meetingId: string,
    insightsEntrySource: InsightsDataFetchSource
) {
    // We are repurposing traceId to represent if the related content insights are in cache.
    // Having a valid traceId means that there was a 3S call made before.
    const traceId = getInsightsTraceId(meetingId);
    const logicalId = getLogicalId(meetingId);

    const filesAndEmailsAreInCache = !!traceId;
    const scenario: SubstrateSearchScenario = getScenarioEndpoint(insightsEntrySource);
    const sessionSettings = getUserConfiguration().SessionSettings;
    if (filesAndEmailsAreInCache) {
        // Logged when client tries to render cached results
        // Refresh the logicalId
        const newLogicalId = getGuid();
        setInsightsLogicalId(meetingId, newLogicalId);

        lazyLogCachedContentRendered.importAndExecute(
            scenario,
            sessionSettings.UserPuid,
            sessionSettings.ExternalDirectoryTenantGuid,
            logicalId,
            newLogicalId,
            null /* conversationId */,
            traceId
        );
    } else {
        // Logged whenver client tries to render recommendations results either from the response even if there are 0 results
        // the time in between the recommendation service call to the results were rendered
        const latency = new Date().getTime() - getInitialRequestTime(meetingId);
        lazyLogResultsRendered.importAndExecute(scenario, logicalId, traceId, latency);
    }
}

/**
 * Logged when an insight suggestion is clicked
 * @param meetingId that insights are shown for
 * @param traceId from recommendation service response
 * @param referenceId of the insight entity returned from recommendation response
 * @param insightsEntrySource where the insigths recommendation service call is triggered from
 */
export function logInsightsSearchEntityActions(
    meetingId: string,
    traceId: string,
    referenceId: string,
    insightsEntrySource: InsightsDataFetchSource
) {
    const scenario: SubstrateSearchScenario = getScenarioEndpoint(insightsEntrySource);
    const sessionSettings = getUserConfiguration().SessionSettings;
    const logicalId = getLogicalId(meetingId);
    lazyLogSearchEntityActions.importAndExecute(
        scenario,
        sessionSettings.UserPuid,
        sessionSettings.ExternalDirectoryTenantGuid,
        logicalId,
        traceId,
        referenceId,
        'EntityClicked',
        null /* metadata */,
        traceId /* overrideKey */
    );
}

/**
 * Logged to indicate what has been displayed in UX
 * @param meetingId that insights are shown for
 * @param insightsEntrySource where the insigths recommendation service call is triggered from
 */
export function logInsightsClientLayout(
    meetingId: string,
    insightsEntrySource: InsightsDataFetchSource
) {
    const scenario: SubstrateSearchScenario = getScenarioEndpoint(insightsEntrySource);
    const traceId = getInsightsTraceId(meetingId);
    const logicalId = getLogicalId(meetingId);
    const sessionSettings = getUserConfiguration().SessionSettings;
    const resultsView = getResultsView(meetingId, traceId, insightsEntrySource);
    const verticalType = convertInsightsEntrySourceEnumToName(insightsEntrySource);
    lazyLogClientLayout.importAndExecute(
        scenario,
        sessionSettings.UserPuid,
        sessionSettings.ExternalDirectoryTenantGuid,
        logicalId,
        traceId,
        getLayoutType(insightsEntrySource),
        resultsView,
        null /* part */,
        verticalType
    );
}

/**
 * Logged when feedback is clicked
 * @param meetingId that insights are shown for
 * @param traceId from recommendation service response
 * @param referenceId of the insight entity returned from recommendation response
 * @param insightsEntrySource where the insights recommendation service call is triggered from
 */
export function logInsightsSearchFeedback(
    meetingId: string,
    traceId: string,
    referenceId: string,
    isUseful: boolean,
    insightsEntrySource: InsightsDataFetchSource
) {
    const scenario: SubstrateSearchScenario = getScenarioEndpoint(insightsEntrySource);
    const logicalId = getLogicalId(meetingId);
    const feedbackResponse = getFeedbackResponse(isUseful);
    lazyLogSearchFeedbackActions.importAndExecute(
        scenario,
        logicalId,
        traceId,
        referenceId,
        feedbackResponse
    );
}

/**
 * Logged when see more files/emails button is clicked
 * @param meetingId
 * @param insightsEntrySource
 */
export function logInsightsSearchActions(
    meetingId: string,
    insightsEntrySource: InsightsDataFetchSource
) {
    const scenario: SubstrateSearchScenario = getScenarioEndpoint(insightsEntrySource);
    const traceId = getInsightsTraceId(meetingId);
    const logicalId = getLogicalId(meetingId);
    const sessionSettings = getUserConfiguration().SessionSettings;
    lazyLogSearchActions.importAndExecute(
        scenario,
        sessionSettings.UserPuid,
        sessionSettings.ExternalDirectoryTenantGuid,
        logicalId,
        traceId,
        'ExpandLinkClicked'
    );
}

function getFeedbackResponse(isUseful: boolean): string {
    return JSON.stringify({
        Type: 'SurveyResponse',
        ResponseValue: isUseful ? 'Yes' : 'No',
        FeedbackString:
            'Is this useful' /* needs to be in sync with string in FeedbackContainer.locstring.json*/,
    });
}

function getLayoutType(insightsEntrySource: InsightsDataFetchSource) {
    switch (insightsEntrySource) {
        case InsightsDataFetchSource.ItemPeek:
        case InsightsDataFetchSource.TimePanelEventDetails:
        case InsightsDataFetchSource.AgendaView:
            return 'Vertical';

        case InsightsDataFetchSource.ReadingPane:
        case InsightsDataFetchSource.TabbedDetailsForm:
        case InsightsDataFetchSource.CalendarCard:
        case InsightsDataFetchSource.Deeplink:
        default:
            return 'Grid';
    }
}

function getResultsView(
    meetingId: string,
    traceId: string,
    insightsEntrySource: InsightsDataFetchSource
): ResultsView[] {
    const resultsView: ResultsView[] = [];
    const insights = getMeetingInsights(meetingId);
    let index = 1;

    // maxInsightsToShow for different entrypoint is different
    const maxInsightsToShow = getMaxInsightsToShowOnInitialRender(insightsEntrySource);
    insights.files.forEach((fileInsight: FileInsightType) => {
        const shown = index < maxInsightsToShow;
        resultsView.push({
            type: 'Entity',
            name: 'Files',
            referenceId: fileInsight.referenceId,
            providerId: traceId,
            position: shown ? index : -1, // position starts from 1
            entityType: 'File',
            placementType: shown ? 'Visible' : 'Hidden',
        });
        index++;
    });

    index = 1;
    insights.emails.forEach((emailInsight: EmailInsightType) => {
        const shown = index < maxInsightsToShow;
        resultsView.push({
            type: 'Entity',
            name: 'Emails',
            referenceId: emailInsight.referenceId,
            providerId: traceId,
            position: shown ? index : -1, // position starts from 1
            entityType: 'Email',
            placementType: shown ? 'Visible' : 'Hidden',
        });
        index++;
    });

    return resultsView;
}
